import React, { useState, useRef, createElement } from 'react';
import { UploadIcon, FileIcon, XIcon, CheckCircleIcon, AlertCircleIcon, DownloadIcon, InfoIcon } from 'lucide-react';
import Papa from 'papaparse';
import { BulkPaymentPreview } from './BulkPaymentPreview';
interface BulkPaymentUploadProps {
  onBatchCreated: () => void;
  simplified?: boolean;
}
export function BulkPaymentUpload({
  onBatchCreated,
  simplified = false
}: BulkPaymentUploadProps) {
  const [file, setFile] = useState<File | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);
  const [parsedData, setParsedData] = useState<any[] | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadStep, setUploadStep] = useState<'upload' | 'preview' | 'confirm'>('upload');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;
    setFile(selectedFile);
    setParseError(null);
    // Check file type
    const fileExt = selectedFile.name.split('.').pop()?.toLowerCase();
    if (fileExt !== 'csv') {
      setParseError('Only CSV files are supported at this time');
      return;
    }
    // Parse CSV
    Papa.parse(selectedFile, {
      header: true,
      skipEmptyLines: true,
      complete: results => {
        if (results.errors.length > 0) {
          setParseError(`Error parsing file: ${results.errors[0].message}`);
          return;
        }
        // Validate required fields
        const requiredFields = ['customerName', 'email', 'amount', 'description'];
        const headers = Object.keys(results.data[0] || {});
        const missingFields = requiredFields.filter(field => !headers.includes(field));
        if (missingFields.length > 0) {
          setParseError(`Missing required fields: ${missingFields.join(', ')}`);
          return;
        }
        setParsedData(results.data);
        setUploadStep('preview');
      },
      error: error => {
        setParseError(`Error parsing file: ${error.message}`);
      }
    });
  };
  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };
  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    const droppedFile = e.dataTransfer.files?.[0];
    if (!droppedFile) return;
    // Update file input value for consistency
    if (fileInputRef.current) {
      const dataTransfer = new DataTransfer();
      dataTransfer.items.add(droppedFile);
      fileInputRef.current.files = dataTransfer.files;
    }
    // Trigger the same handler as file input change
    handleFileChange({
      target: {
        files: e.dataTransfer.files
      }
    } as React.ChangeEvent<HTMLInputElement>);
  };
  const handleRemoveFile = () => {
    setFile(null);
    setParsedData(null);
    setParseError(null);
    setUploadStep('upload');
    // Clear file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  const handleConfirmBatch = () => {
    setIsSubmitting(true);
    // Simulate API call to create batch
    setTimeout(() => {
      setIsSubmitting(false);
      onBatchCreated();
    }, 1500);
  };
  const handleDownloadTemplate = () => {
    const templateHeaders = 'customerName,email,phone,amount,currency,description,reference,dueDate\n';
    const templateData = 'John Doe,john@example.com,+1234567890,100.00,USD,Monthly subscription,INV-001,12/31/2023\n' + 'Jane Smith,jane@example.com,+0987654321,250.00,USD,Consulting services,INV-002,01/15/2024';
    const blob = new Blob([templateHeaders + templateData], {
      type: 'text/csv'
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'bulk_payment_template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };
  if (uploadStep === 'preview' && parsedData) {
    return <BulkPaymentPreview data={parsedData} onBack={() => setUploadStep('upload')} onConfirm={handleConfirmBatch} isSubmitting={isSubmitting} data-id="element-467" />;
  }
  return <div data-id="element-468">
      {!simplified && <div className="p-4 bg-blue-50 text-blue-800 rounded-md flex items-start mb-6" data-id="element-469">
          <InfoIcon size={18} className="mt-0.5 mr-3 flex-shrink-0" data-id="element-470" />
          <div data-id="element-471">
            <p className="text-sm" data-id="element-472">
              Create multiple payment requests at once by uploading a CSV file
              with customer details and payment amounts.
            </p>
            <p className="text-sm mt-2" data-id="element-473">
              Each row in your file will create a separate payment request that
              can be tracked individually.
            </p>
          </div>
        </div>}
      <div className={`
          border-2 border-dashed rounded-lg p-8 
          ${parseError ? 'border-red-300 bg-red-50' : 'border-gray-300 hover:border-blue-400'} 
          transition-colors duration-200 text-center cursor-pointer
        `} onDragOver={handleDragOver} onDrop={handleDrop} onClick={() => fileInputRef.current?.click()} data-id="element-474">
        <input type="file" className="hidden" onChange={handleFileChange} accept=".csv" ref={fileInputRef} data-id="element-475" />
        {file ? <div className="flex flex-col items-center" data-id="element-476">
            <div className="flex items-center bg-gray-100 rounded-md px-4 py-2 mb-4" data-id="element-477">
              <FileIcon size={20} className="text-blue-600 mr-2" data-id="element-478" />
              <span className="text-gray-700 font-medium" data-id="element-479">{file.name}</span>
              <button className="ml-3 text-gray-500 hover:text-red-500" onClick={e => {
            e.stopPropagation();
            handleRemoveFile();
          }} data-id="element-480">
                <XIcon size={16} data-id="element-481" />
              </button>
            </div>
            {parseError ? <div className="flex items-center text-red-600" data-id="element-482">
                <AlertCircleIcon size={18} className="mr-2" data-id="element-483" />
                <span data-id="element-484">{parseError}</span>
              </div> : <div className="flex items-center text-green-600" data-id="element-485">
                <CheckCircleIcon size={18} className="mr-2" data-id="element-486" />
                <span data-id="element-487">File ready for processing</span>
              </div>}
          </div> : <>
            <UploadIcon size={36} className="mx-auto text-gray-400 mb-4" data-id="element-488" />
            <p className="text-gray-600 mb-2" data-id="element-489">
              Drag and drop your CSV file here
            </p>
            <p className="text-gray-500 text-sm mb-4" data-id="element-490">
              or click to browse files
            </p>
            <p className="text-xs text-gray-400" data-id="element-491">Supported formats: .CSV</p>
          </>}
      </div>
      <div className="mt-6 flex justify-between items-center" data-id="element-492">
        <button type="button" onClick={handleDownloadTemplate} className="flex items-center text-blue-600 hover:text-blue-800 text-sm" data-id="element-493">
          <DownloadIcon size={16} className="mr-1.5" data-id="element-494" />
          Download CSV Template
        </button>
        {file && !parseError && <button type="button" onClick={() => setUploadStep('preview')} className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-md transition-colors" data-id="element-495">
            Preview Batch
          </button>}
      </div>
    </div>;
}